export const descendingComparator = <T>(a: T, b: T, orderBy: keyof T) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

export type Order = 'asc' | 'desc';

/**
 * This is a generic type parameter declaration that's using TypeScript's advanced type system features. Let's break it down:
 * 1. Key is a generic type parameter (like a placeholder for a type)
 * 2. extends keyof any is a constraint on what Key can be
 * 
 * The keyof any part is particularly interesting:
 * - keyof is a TypeScript operator that takes an object type and produces a union of its keys
 * - any is TypeScript's top type that can represent any value
 * - keyof any evaluates to string | number | symbol, which are all the possible property key types in JavaScript
 * 
 * So <Key extends keyof any> means that Key must be a type that could be used as an object property key - either a string, number, or symbol.
 * In the context of this sorting utility, this makes sense because the orderBy parameter needs to be a valid property key that can be used to access object properties when comparing them.
 */
export const getComparator = <Key extends keyof any>(
  order: Order,
  orderBy: Key,
): ((
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

export const sortRows = (rows: any[], order: Order, orderBy: keyof any) => {
  return [...rows].sort(getComparator(order, orderBy));
};

export const visibleRowsSorted = (rows: any[], order: Order, orderBy: keyof any, page: number, pageSize: number) => {
  return [...rows]
    .sort(getComparator(order, orderBy))
    .slice(page * pageSize, page * pageSize + pageSize);
};

export const visibleRows = (rows: any[], page: number, pageSize: number) => {
  const p = --page;
  return [...rows]
    .slice(p * pageSize, p * pageSize + pageSize);
};
